import { useState } from "react";

const API_BASE = "http://localhost:8000/api/mtnbenin";
const API_KEY = import.meta.env.VITE_MTN_API_KEY ?? "une_cle_test_123";

type HttpMethod = "GET" | "POST";

interface ApiResult {
  status: number;
  ok: boolean;
  data: unknown;
}

interface TestDef {
  id: string;
  label: string;
  path: string;
  method?: HttpMethod;
  body?: Record<string, unknown>;
}

const COLLECTION_TESTS: TestDef[] = [
  {
    id: "COLLECTION_PENDING",
    label: "COLLECTION_PENDING — Paiement laissé en attente",
    path: "/collection",
    body: {
      amount: 100,
      phone_number: "22967107113",
      payment_id: "1663967834647437878",
      update_payment: true,
    },
  },
  {
    id: "COLLECTION_SUCCESS",
    label: "COLLECTION_SUCCESS — Paiement validé",
    path: "/collection",
    body: {
      amount: 100,
      phone_number: "22967107113",
      payment_id: "16687474747437878",
      update_payment: true,
    },
  },
  {
    id: "COLLECTION_FAIL",
    label: "COLLECTION_FAIL — Paiement rejeté",
    path: "/collection",
    body: {
      amount: 100,
      phone_number: "22967107113",
      payment_id: "1670346020",
      update_payment: true,
    },
  },
  {
    id: "BALANCE_COLLECTION",
    label: "BALANCE_COLLECTION — Solde compte entreprise",
    path: "/balance/collection",
    method: "GET",
  },
];

const DISBURSEMENT_TESTS: TestDef[] = [
  {
    id: "DISBURSEMENT_SUCCESS",
    label: "DISBURSEMENT_SUCCESS — Transfert réussi",
    path: "/disbursement",
    body: {
      amount: 100,
      phone_number: "22997970797",
      reference: "16687474747437878",
      description: "MTN Benin disbursement",
      update_payment: true,
    },
  },
  {
    id: "DISBURSEMENT_FAIL",
    label: "DISBURSEMENT_FAIL — Fonds insuffisants",
    path: "/disbursement",
    body: {
      amount: 100000,
      phone_number: "22997970797",
      reference: "1668747757575757878",
      description: "Test NOT_ENOUGH_FUNDS",
      update_payment: true,
    },
  },
  {
    id: "BALANCE_DISBURSEMENT",
    label: "BALANCE_DISBURSEMENT — Solde compte entreprise",
    path: "/balance/disbursement",
    method: "GET",
  },
];

async function callApi(method: HttpMethod, path: string, body?: Record<string, unknown> | null): Promise<ApiResult> {
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      "X-Internal-API-Key": API_KEY,
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  const text = await res.text();
  let data: unknown;
  try {
    data = JSON.parse(text);
  } catch {
    data = { raw: text };
  }
  return { status: res.status, ok: res.ok, data };
}

// ============================================================
// BLOC : Vérification manuelle de statut
// ============================================================
function StatusChecker() {
  const [kind, setKind] = useState<"collection" | "disbursement">("collection");
  const [txId, setTxId] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [result, setResult] = useState<ApiResult | null>(null);

  async function check(): Promise<void> {
    if (!txId.trim()) {
      alert("Colle un transactionId / referenceId");
      return;
    }
    setLoading(true);
    const path = `/status/${kind}/${txId.trim()}`;
    const r = await callApi("GET", path);
    setResult(r);
    setLoading(false);
  }

  function copyJson(): void {
    navigator.clipboard.writeText(JSON.stringify(result?.data, null, 2));
    alert("JSON copié");
  }

  return (
    <div
      style={{
        border: "2px solid #4a90e2",
        borderRadius: 8,
        padding: 16,
        marginBottom: 24,
        background: "#f0f7ff",
      }}
    >
      <h3 style={{ marginTop: 0 }}>🔎 Vérification manuelle de statut</h3>
      <p style={{ margin: "4px 0 12px", color: "#555", fontSize: 13 }}>
        Colle un <code>transactionId</code> (Collection) ou <code>referenceId</code> (Disbursement) puis clique sur
        Vérifier.
      </p>

      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <select
          value={kind}
          onChange={(e) => setKind(e.target.value as "collection" | "disbursement")}
          style={{ padding: 6 }}
        >
          <option value="collection">Collection</option>
          <option value="disbursement">Disbursement</option>
        </select>

        <input
          type="text"
          value={txId}
          onChange={(e) => setTxId(e.target.value)}
          placeholder="ex: 298b1a38-1d0a-4558-b4a4-48473d33e00b"
          style={{
            flex: 1,
            padding: 6,
            fontFamily: "monospace",
            fontSize: 13,
          }}
        />

        <button onClick={check} disabled={loading}>
          {loading ? "Vérification…" : "Vérifier"}
        </button>
        {result && <button onClick={copyJson}>Copier JSON</button>}
      </div>

      {result && (
        <div>
          <div
            style={{
              color: result.ok ? "green" : "red",
              fontWeight: "bold",
              marginBottom: 6,
            }}
          >
            HTTP {result.status}
          </div>
          <pre
            style={{
              background: "#fff",
              padding: 12,
              borderRadius: 6,
              overflow: "auto",
              fontSize: 12,
            }}
          >
            {JSON.stringify(result.data, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}

// ============================================================
// TestCard
// ============================================================
interface TestCardProps {
  test: TestDef;
  onResult?: (id: string, r: ApiResult) => void;
}

function TestCard({ test, onResult }: TestCardProps) {
  const [body, setBody] = useState<string>(test.body ? JSON.stringify(test.body, null, 2) : "");
  const [loading, setLoading] = useState<boolean>(false);
  const [result, setResult] = useState<ApiResult | null>(null);

  const method: HttpMethod = test.method ?? "POST";

  async function run(): Promise<void> {
    setLoading(true);
    let parsed: Record<string, unknown> | null = null;
    if (body) {
      try {
        parsed = JSON.parse(body);
      } catch {
        alert("JSON invalide");
        setLoading(false);
        return;
      }
    }
    const r = await callApi(method, test.path, parsed);
    setResult(r);
    onResult?.(test.id, r);
    setLoading(false);
  }

  async function checkStatus(): Promise<void> {
    const d = result?.data as
      | { financialTransactionId?: string; externalId?: string; transaction_id?: string }
      | undefined;
    const txId = d?.financialTransactionId ?? d?.externalId ?? d?.transaction_id;
    if (!txId) {
      alert("Pas de transaction ID dans la réponse");
      return;
    }
    const path = test.path.includes("disbursement") ? `/status/disbursement/${txId}` : `/status/collection/${txId}`;
    const r = await callApi("GET", path);
    setResult(r);
    onResult?.(test.id + "_STATUS", r);
  }

  function copyJson(): void {
    navigator.clipboard.writeText(JSON.stringify(result?.data, null, 2));
    alert("JSON copié");
  }

  return (
    <div
      style={{
        border: "1px solid #ddd",
        borderRadius: 8,
        padding: 16,
        marginBottom: 16,
        background: "#fff",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <h3 style={{ margin: 0 }}>{test.label}</h3>
        <code style={{ color: "#666" }}>
          {method} {test.path}
        </code>
      </div>

      {test.body && (
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={8}
          style={{
            width: "100%",
            marginTop: 10,
            fontFamily: "monospace",
            fontSize: 13,
            padding: 8,
          }}
        />
      )}

      <div style={{ marginTop: 10, display: "flex", gap: 8 }}>
        <button onClick={run} disabled={loading}>
          {loading ? "Envoi…" : "Envoyer"}
        </button>
        {result && method !== "GET" && <button onClick={checkStatus}>Vérifier statut</button>}
        {result && <button onClick={copyJson}>Copier JSON</button>}
      </div>

      {result && (
        <div style={{ marginTop: 12 }}>
          <div
            style={{
              color: result.ok ? "green" : "red",
              fontWeight: "bold",
              marginBottom: 6,
            }}
          >
            HTTP {result.status}
          </div>
          <pre
            style={{
              background: "#f6f8fa",
              padding: 12,
              borderRadius: 6,
              overflow: "auto",
              fontSize: 12,
            }}
          >
            {JSON.stringify(result.data, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}

// ============================================================
// Page principale
// ============================================================
export default function MtnTests(): JSX.Element {
  const [tab, setTab] = useState<"COL" | "DIS">("COL");
  const tests = tab === "COL" ? COLLECTION_TESTS : DISBURSEMENT_TESTS;

  return (
    <div style={{ padding: 24, maxWidth: 1000, margin: "0 auto" }}>
      <h1>MTN Bénin — Tests Collection / Disbursement</h1>

      <StatusChecker />

      <div style={{ marginBottom: 20 }}>
        <button onClick={() => setTab("COL")} disabled={tab === "COL"} style={{ marginRight: 8 }}>
          Collection
        </button>
        <button onClick={() => setTab("DIS")} disabled={tab === "DIS"}>
          Disbursement
        </button>
      </div>

      {tests.map((t) => (
        <TestCard key={t.id} test={t} />
      ))}
    </div>
  );
}
